import type * as React from "react";
import { EmptyState } from "@/ui/molecules";
import { formatMonthLabel } from "./submissionContext";

/**
 * File types spelled out, keyed by the backend `FileType` wire value.
 *
 * Deliberately not the abbreviations `FileReviewTabs` uses for its navigation:
 * this copy is a sentence, and "No TB for March 2026" reads as jargon in one.
 */
const FILE_TYPE_NAMES: Record<string, string> = {
    trial_balance: "trial balance",
    general_ledger: "general ledger",
    open_trial_balance: "open trial balance",
    financial_journals: "financial journals",
    tb_gl: "trial balance or general ledger",
};

export type TFileReviewEmptyMonthProps = {
    /** Backend `FileType` wire value of the review type currently selected. */
    fileType: string;
    /** The selected month, `YYYY-MM`. */
    period: string;
};

/**
 * What the Review panel shows for a month the submission has no file for.
 *
 * No upload call to action: uploading is its own journey, and most of the roles
 * who can reach this page cannot upload a TB or GL at all — a button they are not
 * allowed to press is worse than no button.
 */
export function FileReviewEmptyMonth(props: TFileReviewEmptyMonthProps): React.ReactElement {
    const { fileType, period } = props;
    const typeName = FILE_TYPE_NAMES[fileType] ?? fileType.replace(/_/g, " ");
    const monthLabel = formatMonthLabel(period);

    return (
        <div data-testid="file-review-empty-month">
            <EmptyState
                title={`No ${typeName} for ${monthLabel}`}
                description={`Nothing has been submitted for ${monthLabel}. Pick another month to carry on reviewing.`}
            />
        </div>
    );
}
